/**
 * <bbq-gallery-rail> — the BBQGuys thumbnail rail, driving Horizon's product slideshow.
 *
 * Rendered by snippets/bbq-gallery-rail.liquid, always as a child of <media-gallery>. The rail
 * never owns slide state: it calls `Slideshow.select()` and then listens for the `slideshow:select`
 * event that every path back out of the slideshow emits — button, scroll, drag, and the zoom
 * dialog's own thumbnails (media-gallery.js:65). That is why dragging the main image or closing
 * the zoom dialog on a different image leaves the rail correct.
 *
 * Deliberately extends HTMLElement rather than @theme/component: the rail needs one delegated
 * click handler and one keydown handler, not a refs MutationObserver, and keeping it off the
 * Component base means it has no dependency on the declarative `on:` binding path or on the
 * custom-element upgrade race that path has to work around (component.js:281).
 *
 * ARIA contract, which is the entire reason this file exists — see the snippet's header for the
 * 62 violations Horizon's own thumbnails produce:
 *   - the rail is role="toolbar", so it is ONE tab stop;
 *   - Up/Down (or Left/Right when horizontal) move focus, Home/End jump, and Enter/Space activate,
 *     which is native <button> behaviour. Arrow keys deliberately do NOT change the slide: moving
 *     focus is not choosing, and auto-selecting would fire a slide change per keypress;
 *   - selection is aria-pressed, never aria-selected, which is invalid on role="button";
 *   - hidden overflow thumbnails carry the `hidden` attribute, so they leave the tab order and the
 *     accessibility tree together. Every query below filters on it.
 */

const SELECTED_ATTR = 'aria-pressed';

class BbqGalleryRail extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLElement | null} */
  #gallery = null;

  /** How many thumbnails are currently revealed. Never below `data-visible`. */
  #shown = 0;

  connectedCallback() {
    this.#gallery = this.closest('media-gallery');

    // The rail is rendered BEFORE the slideshow in the snippet, so on the very first upgrade the
    // slideshow element may not be parsed yet. Give it one frame before giving up, and then hide
    // rather than remove — dead controls must not stay focusable, but a transient miss must not
    // destroy markup the next `media-gallery` replacement would have re-synced.
    if (!this.#gallery || !this.#slideshow) {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#gallery = this.closest('media-gallery');
        if (this.#gallery && this.#slideshow) this.#attach();
        else this.hidden = true;
      });
      return;
    }

    this.#attach();
  }

  #attach() {
    this.hidden = false;
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.#gallery.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });

    this.#shown = this.#base;

    // The server renders index 0 pressed because `sorted_media` puts the selected variant's media
    // first and the slideshow defaults to `initial-slide` 0. Re-sync anyway: a deep link or a
    // restored scroll position can land the slideshow elsewhere before this runs.
    this.#sync(this.#slideshow?.current ?? 0, { focus: false });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * The slideshow this rail drives.
   *
   * `customElements.upgrade` closes the same race component.js:281 documents: on a slow connection
   * the element can be parsed and connected before slideshow.js has executed, and `select` would
   * be undefined for the first clicks. It is synchronous, idempotent, and a no-op if the class is
   * not registered yet — in which case we return null and the click is ignored rather than throwing.
   *
   * @returns {(HTMLElement & { select: Function, current: number }) | null}
   */
  get #slideshow() {
    const element = this.#gallery?.querySelector('slideshow-component');
    if (!element) return null;

    if (typeof (/** @type {any} */ (element).select) !== 'function') {
      customElements.upgrade(element);
    }

    return typeof (/** @type {any} */ (element).select) === 'function'
      ? /** @type {any} */ (element)
      : null;
  }

  /** Every thumbnail button, hidden ones included, in slide order. */
  get #thumbs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.bbq-gallery-rail__thumb')]);
  }

  /** The buttons a keyboard user can move between right now: visible thumbnails, then the toggle. */
  get #stops() {
    return /** @type {HTMLButtonElement[]} */ ([
      ...this.querySelectorAll('.bbq-gallery-rail__thumb:not([hidden]), .bbq-gallery-rail__more'),
    ]);
  }

  /** @returns {HTMLButtonElement | null} */
  get #toggle() {
    return this.querySelector('[data-rail-toggle]');
  }

  /** Thumbnails visible at rest, as rendered by the snippet. */
  get #base() {
    return Number(this.dataset.visible) || this.#thumbs.length;
  }

  /**
   * How many more each press of the disclosure reveals.
   *
   * MEASURED, not chosen for tidiness: revealing everything at once on this catalogue's 62-image
   * product produced a 4528px rail — 4.5 viewports — inside a `sticky-content` column, which
   * both breaks the sticky behaviour and buries the buy box. A step of 12 keeps each reveal near
   * the height of the gallery beside it. 181 of 2,555 products carry 20+ images, so this is the
   * normal case for a meaningful slice of the catalogue, not a pathological edge.
   */
  get #step() {
    return Number(this.dataset.step) || 12;
  }

  get #allShown() {
    return this.#shown >= this.#thumbs.length;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const toggle = target.closest('[data-rail-toggle]');
    if (toggle) {
      this.#setShown(this.#allShown ? this.#base : this.#shown + this.#step);
      return;
    }

    const thumb = /** @type {HTMLButtonElement | null} */ (target.closest('.bbq-gallery-rail__thumb'));
    if (!thumb) return;

    const index = Number(thumb.dataset.index);
    if (!Number.isInteger(index)) return;

    // Passing the event through marks the selection user-initiated in the SlideshowSelectEvent,
    // which is what Horizon's own controls do. The resulting event syncs the rail; nothing here
    // writes aria-pressed directly, so the rail can never disagree with the slideshow.
    this.#slideshow?.select(index, event);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const stops = this.#stops;
    const current = stops.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (current === -1) return;

    const horizontal = this.getAttribute('aria-orientation') === 'horizontal';
    const forward = horizontal ? 'ArrowRight' : 'ArrowDown';
    const backward = horizontal ? 'ArrowLeft' : 'ArrowUp';

    let next = -1;

    switch (event.key) {
      case forward:
        next = Math.min(current + 1, stops.length - 1);
        break;
      case backward:
        next = Math.max(current - 1, 0);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = stops.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#focus(stops[next]);
  };

  /** @param {CustomEvent<{ index: number }>} event */
  #handleSlideshowSelect = (event) => {
    const index = event.detail?.index;
    if (!Number.isInteger(index)) return;

    // Only move focus when the rail already had it — a scroll or drag on the main image must not
    // steal focus into the rail.
    const hadFocus = this.contains(document.activeElement);
    this.#sync(index, { focus: hadFocus });
  };

  /**
   * Point the rail at a slide index: pressed state, roving tabindex, and overflow visibility.
   *
   * @param {number} index - The selected slide index.
   * @param {object} [options]
   * @param {boolean} [options.focus] - Move DOM focus to the newly selected thumbnail.
   */
  #sync(index, { focus = false } = {}) {
    const thumbs = this.#thumbs;
    const selected = thumbs[index] ?? null;

    // A selected thumbnail that is still collapsed would be pressed but invisible. Expand instead
    // of silently disagreeing with the main image.
    if (selected?.hasAttribute('hidden')) this.#setShown(index + 1, { focus: false });

    for (const thumb of thumbs) {
      thumb.setAttribute(SELECTED_ATTR, String(thumb === selected));
    }

    // Roving tabindex: exactly one stop is tabbable, and it is the selected thumbnail whenever the
    // selected thumbnail is visible. Falling back to the first stop keeps the rail reachable if the
    // index is out of range.
    const tabbable = selected && !selected.hasAttribute('hidden') ? selected : this.#stops[0] ?? null;

    for (const stop of this.#stops) {
      stop.tabIndex = stop === tabbable ? 0 : -1;
    }

    if (focus && tabbable) this.#focus(tabbable);
  }

  /**
   * Reveal the first `count` thumbnails, clamped to [`data-visible`, total].
   *
   * Collapsing never hides the SELECTED thumbnail — the rail would then show a state the main
   * image contradicts — so a collapsed rail can be one slot taller than `data-visible` while a
   * deep image is selected. That is the honest rendering of the state, not a bug.
   *
   * @param {number} count
   * @param {object} [options]
   * @param {boolean} [options.focus] - Keep focus inside the rail after the change.
   */
  #setShown(count, { focus = true } = {}) {
    const toggle = this.#toggle;
    if (!toggle) return;

    const thumbs = this.#thumbs;
    const total = thumbs.length;

    this.#shown = Math.min(Math.max(count, this.#base), total);

    thumbs.forEach((thumb, index) => {
      if (!thumb.hasAttribute('data-overflow')) return;

      const keep = index < this.#shown || thumb.getAttribute(SELECTED_ATTR) === 'true';
      thumb.toggleAttribute('hidden', !keep);
      if (!keep) thumb.tabIndex = -1;
    });

    const remaining = total - this.#shown;
    const done = remaining <= 0;
    const next = Math.min(this.#step, remaining);

    // `aria-expanded` tracks whether anything beyond the resting set is disclosed; the accessible
    // name carries what the press will actually do, which is the part that changes per page.
    toggle.setAttribute('aria-expanded', String(this.#shown > this.#base));
    toggle.setAttribute(
      'aria-label',
      done ? 'Show fewer images' : `Show ${next} more images, ${remaining} remaining`
    );
    toggle.classList.toggle('bbq-gallery-rail__more--expanded', done);

    const countEl = toggle.querySelector('.bbq-gallery-rail__more-count');
    if (countEl) countEl.textContent = done ? '' : `+${remaining}`;

    // Focus is on the toggle when this runs from a click; collapsing can pull the tabbable
    // thumbnail out of the DOM order, so re-derive the roving tabindex against what is left.
    const selected = thumbs.findIndex((thumb) => thumb.getAttribute(SELECTED_ATTR) === 'true');
    if (selected !== -1) this.#sync(selected, { focus: false });

    if (focus) toggle.focus();
  }

  /** @param {HTMLButtonElement | undefined} stop */
  #focus(stop) {
    if (!stop) return;

    for (const other of this.#stops) {
      other.tabIndex = other === stop ? 0 : -1;
    }

    stop.focus();
  }
}

if (!customElements.get('bbq-gallery-rail')) {
  customElements.define('bbq-gallery-rail', BbqGalleryRail);
}
